/**
 * Atar un diseño del CMS a una colección: cada pieza marcada con un papel
 * (`cms.title`, `cms.image`…) toma el campo de la colección que lo cumple.
 * Aparte de `cmsDesigns.ts` (que pide los diseños a la biblioteca) para que el
 * estado del editor lo use sin traerse la red: las páginas de una colección
 * nueva nacen con su diseño (ver `cmsDefaultDesigns.ts`).
 */
import type { CmsSlotRole } from '@webbuilder/schemas'
import type { CmsCollection, CmsField, ElementNode } from '../store/editor.store'

/**
 * Qué campo cumple cada papel en esta colección.
 *
 * Primero el campo de sistema de siempre —toda colección los trae (`title`,
 * `image`, `description`, `content`, `created`, `categories`)—, salvo que esté
 * oculto; si no, el primero del tipo que toca.
 */
export function cmsRoleFields(collection: Pick<CmsCollection, 'fields' | 'hiddenFields'>): Partial<Record<CmsSlotRole, CmsField>> {
  const hidden = new Set(collection.hiddenFields ?? [])
  const visible = collection.fields.filter((field) => !hidden.has(field.id))
  const pick = (systemId: string, types: CmsField['type'][]) =>
    visible.find((field) => field.id === systemId) ?? visible.find((field) =>
      !['status', 'slug', 'created', 'createdAt', 'updatedAt'].includes(field.id) && types.includes(field.type))
  const result: Partial<Record<CmsSlotRole, CmsField>> = {}
  const set = (role: CmsSlotRole, field: CmsField | undefined) => { if (field) result[role] = field }
  set('title', pick('title', ['title']))
  set('image', pick('image', ['image', 'multiImage']))
  set('description', pick('description', ['text']))
  set('content', pick('content', ['richText']))
  set('date', pick('created', ['date']))
  set('categories', pick('categories', ['option']))
  return result
}

/** Collection detail actions consistently use the secondary button token. */
export function normalizeCmsDetailAction(node: ElementNode): ElementNode {
  if (node.aiSlot !== 'cms.link') return node
  return {
    ...node,
    type: 'button',
    themeRole: 'buttonSecondary',
    styleRefs: { ...node.styleRefs, button: 'button-secondary' },
    iconName: undefined,
    content: node.content?.replace(/\s*[→➜➝➞➔↗]\s*$/u, '').trim(),
  }
}

/**
 * El diseño atado a la colección.
 *
 * `itemPageSlug` es la dirección de la página de ficha (`/equipo/[slug]`), para
 * los enlaces de la lista; `collectionPageSlug`, la de la lista, para el
 * «volver» de la ficha.
 */
export function bindCmsSlots(
  elements: ElementNode[],
  collection: CmsCollection,
  pages: { itemPageSlug?: string | undefined; collectionPageSlug?: string | undefined } = {},
): ElementNode[] {
  const roles = cmsRoleFields(collection)
  const hasStatus = collection.fields.some((field) => field.id === 'status')
  const hasCreated = collection.fields.some((field) => field.id === 'created')

  const visit = (node: ElementNode): ElementNode | null => {
    const id = crypto.randomUUID()
    const children = (node.children ?? []).map(visit).filter((child): child is ElementNode => child !== null)
    const next: ElementNode = { ...node, id, schemaId: node.schemaId ?? id, children }
    const slot = node.aiSlot ?? ''
    if (!slot.startsWith('cms.')) return next
    const role = slot.slice(4)

    if (role === 'list') {
      return {
        ...next,
        cmsRepeat: {
          ...(node.cmsRepeat ?? { source: 'projectContent' as const, collection: '' }),
          source: 'projectContent',
          collection: collection.id,
          /* Lo más nuevo primero y sólo lo público, como la lista de siempre. */
          ...(hasCreated ? { sortField: 'created', sortDirection: 'desc' as const } : {}),
          ...(hasStatus ? { filterField: 'status', filterValue: 'Público' } : {}),
        },
      }
    }
    if (role === 'link') {
      return {
        ...normalizeCmsDetailAction(next),
        interaction: {
          action: 'link',
          cmsLink: { source: 'projectContent', collection: '', slugField: 'slug', ...(pages.itemPageSlug ? { pageSlug: pages.itemPageSlug } : {}) },
        },
      }
    }
    if (role === 'backLink') {
      return pages.collectionPageSlug
        ? { ...next, href: pages.collectionPageSlug, interaction: { action: 'link', href: pages.collectionPageSlug } }
        : null
    }
    if (role === 'collectionName' || role === 'collectionIntro') {
      return {
        ...next,
        contentBinding: { source: 'projectContent', collection: collection.id, field: role === 'collectionName' ? '__name' : '__description' },
        content: role === 'collectionName' ? collection.name : '',
      }
    }
    const field = roles[role as CmsSlotRole]
    /* La colección no tiene nada para este papel: la pieza sobra. */
    if (!field) return null
    return {
      ...next,
      contentBinding: { source: 'projectContent', collection: '', field: field.id },
      ...(node.type === 'image' ? { src: '' } : { content: '' }),
      ...(field.type === 'richText' ? { contentMode: 'html' as const } : {}),
    }
  }
  return elements.map(visit).filter((node): node is ElementNode => node !== null)
}
